/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/

import React, { useEffect } from 'react';
import cx from 'classnames';
import { get, isArray } from 'lodash';

import AssetPreview from '../AssetPreview';
import UploadPreview from '../UploadPreview';
import useAssetDropzone from '@broadleaf/admin-components/dist/common/hooks/useAssetDropzone';
import usePrevious from '@broadleaf/admin-components/dist/common/hooks/usePrevious';

/**
 * Component used to provide a dropzone for uploading files and assets.
 *
 * ```js
 * <AssetDropzone
 *   handleUpload={
 *     file => {
 *       // returns a Promise that resolves when the upload finishes
 *     }
 *   }
 *   style={{ width: 240, height: 240 }}
 * />
 * ```
 *
 * @see See `common/hooks/useAssetDropzone` for the hook that drives the internal state
 */
export const AssetDropzone: React.FC<AssetDropzoneProps> = props => {
  const {
    clearUploads,
    isUploading,
    state,
    getInputProps,
    getRootProps
  } = useAssetDropzone(props);

  const wasUploading = usePrevious(isUploading);

  useEffect(() => {
    // clear uploads when we finish uploading
    if (wasUploading && !isUploading) {
      clearUploads();
    }
  });

  const renderUploadPreview = () => {
    if (!state.uploadIds || !state.uploadIds.length) return null;
    return state.uploadIds.map(uploadId => (
      <UploadPreview
        {...state.uploadsById[uploadId]}
        className="AssetDropzone tw-mx-2"
        key={uploadId}
        previewStyle={props.previewStyle}
        previewClassName={cx(props.previewClassName, ' tw-w-full tw-h-full')}
        srcKey={props.srcKey}
        typeKey={props.typeKey}
      />
    ));
  };

  const renderAssetPreview = () => {
    if (props.value) return <AssetDropzoneValue {...props} />;

    return <AssetDropzoneEmpty message={props.emptyMessage} />;
  };

  return (
    <div
      {...getRootProps({
        className: cx(
          'tw-relative tw-flex tw-items-center tw-justify-center tw-text-center tw-text-gray-400 tw-font-bold tw-bg-gray-100 tw-border-2 tw-border-dashed tw-rounded tw-outline-none AssetDropzone',
          { 'tw-overflow-x-scroll': props.multiple },
          props.className
        ),
        style: props.style
      })}
    >
      <input {...getInputProps()} />
      {isUploading ? renderUploadPreview() : renderAssetPreview()}
    </div>
  );
};

export interface AssetDropzoneProps {
  /** The accepted mimeTypes for files that this dropzone supports. */
  accept?: string | Array<string>;
  /** Class name for the outer wrapper div. */
  className?: string;
  /** Whether or not this dropzone is currently disabled. */
  disabled?: boolean;
  /** The message to render when the dropzone is empty. */
  emptyMessage?: string;
  /** The handler used for uploading the asset. */
  handleUpload?: Function;
  /** Whether or not uploading multiple files. */
  multiple?: boolean;
  /** Whether or not click events should trigger the file dialog. */
  noClick?: boolean;
  /** Called when all of the uploads are finished. */
  onFinishUploads?: (value: any[]) => any;
  /** Handler called when a file does not match the `accept` prop types and is rejected. */
  onRejectFile?: Function;
  /** The className to use on the AssetPreview component. */
  previewClassName?: string;
  /** Styles for the AssetPreview component. */
  previewStyle?: object;
  /** The property key that represents the asset's src, defaults to "src". */
  srcKey?: string;
  /** Styles for the outer wrapper div. */
  style?: object;
  /** The property key that represents the asset's type, defaults to "type". */
  typeKey?: string;
  /** The current asset value. */
  value?: object | Array<object>;
}

AssetDropzone.defaultProps = {
  disabled: false,
  emptyMessage: 'Drop file here, or click to upload',
  multiple: false,
  noClick: false,
  srcKey: 'url',
  typeKey: 'type',
  value: null
};

const AssetDropzoneValue = props => {
  if (isArray(props.value)) {
    return props.value.map((value, index) => (
      <AssetPreview
        className={cx('tw-h-full tw-w-full', props.previewClassName)}
        key={index}
        src={get(value, props.srcKey)}
        assetStyle={props.assetStyle}
        style={props.previewStyle}
        type={get(value, props.typeKey)}
      />
    ));
  }

  return (
    <AssetPreview
      className={cx('tw-h-full tw-w-full', props.previewClassName)}
      src={get(props.value, props.srcKey)}
      assetStyle={props.assetStyle}
      style={props.previewStyle}
      type={get(props.value, props.typeKey)}
    />
  );
};

export const AssetDropzoneEmpty: React.FC<{ message: string }> = props => {
  return <div className="tw-w-full tw-text-sm">{props.message}</div>;
};

export default AssetDropzone;
